// ─────────────────────────────────────────────────────────────────────────────
// ReadyStep (3/3) — the created node and everything it needs to come up.
//
// The three values the node-core binary is configured with are node_id,
// enrollment_token and gateway_url. They are all real and come from the 201 —
// and they are also exactly what the generated node-core.json contains, so the
// operator can either copy them by hand or download the file.
//
// THE TOKEN IS NO LONGER UNRECOVERABLE. It used to be shown here exactly once
// with no way to get it back, which is why this screen resists dismissal. That is
// now a soft guarantee rather than a hard one: POST /enterprise-nodes/:id/config
// re-serves the same token while it is still valid, and issues a fresh one (and
// restores the node) once it has expired — for any node that is not currently
// online. Copying it here is still the fast path; losing it is no longer fatal
// and no longer means deleting and re-adding the node.
//
// A node can arrive here WITH warnings: an unreachable address or a failed geo
// lookup does not prevent creation. Those are advisories about follow-up work, not
// failures, and are worded that way.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import styles from "./AddNodeWizard.module.css";
import { CheckCircleIcon, DownloadIcon } from "./icons";
import { dash } from "../../format";
import useNodeCoreDownload from "../../hooks/useNodeCoreDownload";
import { formatBytes, startCommand } from "../nodeCoreDownload";

/** Local time, or the raw value if it isn't a parsable timestamp. */
function fmtExpiry(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard is blocked on plain http in some browsers. The value is on
      // screen and selectable, so don't claim a copy that didn't happen.
      setCopied(false);
    }
  };

  return (
    <button type="button" className={styles.btnGhost} onClick={copy}>
      {copied ? "Copied" : label}
    </button>
  );
}

export default function ReadyStep({ created, form, onClose }) {
  const expiry = fmtExpiry(created.expires_at);

  const {
    loadBinaryMeta, getBinary, binaryLoading, binaryError, binaryMeta,
    getConfig, configLoading, configError, configResult,
  } = useNodeCoreDownload(created.node_id);

  // Read-only and side-effect free, so the real size can be on screen before the
  // operator decides to download.
  useEffect(() => { loadBinaryMeta(); }, [loadBinaryMeta]);

  // Server values win over what was typed: the name is echoed back, and geo was
  // detected rather than supplied.
  const rows = {
    "Node Name":  dash(created.name ?? form.name),
    "Node ID":    dash(created.node_id),
    "IP Address": dash(form.ip),
    "Region":     dash(created.geo?.region),
    "Country":    dash(created.geo?.country),
  };

  return (
    <div className={styles.step}>
      <div className={styles.readyHead}>
        <span className={styles.readyIcon}><CheckCircleIcon /></span>
        <div className={styles.stepHead}>
          <h2 className={styles.title} id="add-node-title">Node Registered</h2>
          <p className={styles.subtitle}>
            Deploy Node Core with the values below. It must enroll
            {expiry ? ` before ${expiry}` : " within one hour"}.
          </p>
        </div>
      </div>

      <dl className={styles.summary}>
        {Object.entries(rows).map(([key, value]) => (
          <div key={key} className={styles.summaryRow}>
            <dt className={styles.summaryKey}>{key}</dt>
            <dd className={styles.summaryVal}>{value}</dd>
          </div>
        ))}
      </dl>

      {/* ── The credential. Copy it, or take it in the config file below. ────── */}
      <div className={styles.field}>
        <label className={styles.label}>Enrollment token</label>
        <code className={styles.command}>{created.enrollment_token}</code>
        <CopyButton value={created.enrollment_token} label="Copy token" />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Gateway URL</label>
        <code className={styles.command}>{created.gateway_url}</code>
      </div>

      {/* The config file carries all three values, so the start command needs no
          flags beyond pointing at it. */}
      <code className={styles.command}>
        <span className={styles.prompt}>$</span>
        {startCommand(configResult?.filename)}
      </code>

      {/* Advisories, not failures — the node was created regardless. */}
      {created.warnings.map((w) => (
        <p key={w} className={styles.subtitle}>{w}</p>
      ))}

      <dl className={styles.summary}>
        {Object.entries({
          Platform: binaryMeta?.platform ?? "linux",
          Size:     dash(formatBytes(binaryMeta?.size_bytes)),
        }).map(([key, value]) => (
          <div key={key} className={styles.summaryRow}>
            <dt className={styles.summaryKey}>{key}</dt>
            <dd className={styles.summaryVal}>{value}</dd>
          </div>
        ))}
      </dl>

      {binaryError && <p className={styles.subtitle}>{binaryError}</p>}
      {configError && <p className={styles.subtitle}>{configError}</p>}

      {/* A browser can suppress a download silently — an ad/popup blocker
          intercepting it, or a click whose user activation had lapsed. Nothing is
          thrown in either case, so offer a real link as the fallback. */}
      {binaryMeta?.binary_url && (
        <p className={styles.subtitle}>
          Download didn&apos;t start?{" "}
          <a href={binaryMeta.binary_url} download="node-core" rel="noopener">
            Use this direct link
          </a>{" "}
          (valid for {Math.max(0, Math.round((binaryMeta.expires_in_seconds ?? 0) / 60))} min).
        </p>
      )}
      {configResult && (
        <p className={styles.subtitle}>
          Saved <code>{configResult.filename}</code> — it contains the node id,
          enrollment token and gateway URL.
        </p>
      )}

      <div className={styles.footer}>
        <button type="button" className={styles.btnGhost} onClick={onClose}>
          Close
        </button>
        <button
          type="button"
          className={styles.btnGhost}
          onClick={getConfig}
          disabled={configLoading}
        >
          <DownloadIcon /> {configLoading ? "Generating…" : "Download Config"}
        </button>
        <button
          type="button"
          className={styles.btnPrimary}
          onClick={getBinary}
          disabled={binaryLoading}
        >
          <DownloadIcon /> {binaryLoading ? "Preparing…" : "Download Binary"}
        </button>
      </div>
    </div>
  );
}
